import { MODULE_NAME } from './const';
import StoreModuleService from '../StoreModuleService';
import BillingService from '@/components/Billing/billing-service';
import { BillingPeriod, ProductType } from '@/components/Billing/billing-types';
import { arrayToObject } from '@/utils/helpers';
const buildGetterObject = (getter) => StoreModuleService.buildGetterObject(MODULE_NAME, getter);

// getters declarations
export const usePlans = buildGetterObject('usePlans');
export const useProduct = buildGetterObject('useProduct');
export const usePlanById = buildGetterObject('usePlanById');
export const useProducts = buildGetterObject('useProducts');
export const useTeamPlans = buildGetterObject('useTeamPlans');
export const useEntitlement = buildGetterObject('useEntitlement');
export const useUsageHistory = buildGetterObject('useUsageHistory');
export const useEntitlements = buildGetterObject('useEntitlements');
export const useCustomerData = buildGetterObject('useCustomerData');
export const useCreditsPlans = buildGetterObject('useCreditsPlans');
export const useCustomerPlans = buildGetterObject('useCustomerPlans');
export const useBillingPeriod = buildGetterObject('useBillingPeriod');
export const useSubscriptions = buildGetterObject('useSubscriptions');
export const useHasCapability = buildGetterObject('useHasCapability');
export const useUsageAnalytics = buildGetterObject('useUsageAnalytics');
export const usePaymentMethods = buildGetterObject('usePaymentMethods');
export const useIsAnnuallyPlan = buildGetterObject('useIsAnnuallyPlan');
export const usePricingPreview = buildGetterObject('usePricingPreview');
export const usePricingPreviews = buildGetterObject('usePricingPreviews');
export const useAutomationPlans = buildGetterObject('useAutomationPlans');
export const useEntitlementPlans = buildGetterObject('useEntitlementPlans');
export const useAccountProductIds = buildGetterObject('useAccountProductIds');
export const usePlansByProductType = buildGetterObject('usePlansByProductType');
export const useFeatureUsageHistory = buildGetterObject('useFeatureUsageHistory');
export const useCreditsSubscription = buildGetterObject('useCreditsSubscription');
export const useDefaultPaymentMethod = buildGetterObject('useDefaultPaymentMethod');
export const useOrganizationSettings = buildGetterObject('useOrganizationSettings');
export const useAutomationSubscription = buildGetterObject('useAutomationSubscription');
export const useDefaultPaymentMethodId = buildGetterObject('useDefaultPaymentMethodId');
export const useUpNextRequiredPlanToUse = buildGetterObject('useUpNextRequiredPlanToUse');
export const useEntitlementsLastRefresh = buildGetterObject('useEntitlementsLastRefresh');
export const useEntitlementsIsRefreshing = buildGetterObject('useEntitlementsIsRefreshing');
export const useTeamFeaturesSubscription = buildGetterObject('useTeamFeaturesSubscription');
export const useSubscriptionByProductType = buildGetterObject('useSubscriptionByProductType');
export const useUpNextRequiredPlanToUseData = buildGetterObject('useUpNextRequiredPlanToUseData');
export const useCustomerFeatureSubscription = buildGetterObject('useCustomerFeatureSubscription');
export const useFeatureUsageAnalyticsHistory = buildGetterObject('useFeatureUsageAnalyticsHistory');
export const useProductAutomatedSubscriptionChanges = buildGetterObject('useProductAutomatedSubscriptionChanges');
export const useOveragesEstimationCosts = buildGetterObject('useOveragesEstimationCosts');

const getters = {
	[useProducts.getter]: (state) => state.products,
	[useEntitlements.getter]: (state) => state.entitlements,
	[useUsageHistory.getter]: (state) => state.usageHistory,
	[useCustomerData.getter]: (state) => state.customerData,
	[useSubscriptions.getter]: (state) => state.subscriptions,
	[usePaymentMethods.getter]: (state) => state.paymentMethods,
	[useIsAnnuallyPlan.getter]: (state) => state.isAnnuallyPlan,
	[useUsageAnalytics.getter]: (state) => state.usageAnalytics,
	[usePricingPreviews.getter]: (state) => state.pricingPreview,
	[useEntitlementPlans.getter]: (state) => state.entitlementPlans,
	[useOrganizationSettings.getter]: (state) => state.organizationSettings,
	[useDefaultPaymentMethodId.getter]: (state) => state.defaultPaymentMethodId,
	[useEntitlementsLastRefresh.getter]: (state) => state.entitlementsLastRefresh,
	[useEntitlementsIsRefreshing.getter]: (state) => state.entitlementsIsRefreshing,
	[useProductAutomatedSubscriptionChanges.getter]: (state) => (productType) => state.productAutomatedSubscriptionChanges[productType],
	//
	[usePricingPreview.getter]: (state, getters) => (pricingPreviewInput) =>
		getters[usePricingPreviews.getter][BillingService.buildPricingPreviewKey(pricingPreviewInput)],
	[usePlanById.getter]: (state, getters) => (planId) => getters[usePlans.getter][planId],
	[useProduct.getter]: (state, getters) => (productType) => getters[useProducts.getter][productType],
	[usePlansByProductType.getter]: (state, getters) => (productType) => getters[useProducts.getter][productType].plans,
	[usePlansByProductType.getter]: (state, getters) => (productType) => getters[useProducts.getter][productType].plans,
	[useCreditsSubscription.getter]: (state, getters) => getters[useSubscriptionByProductType.getter](ProductType.Credits),
	[useAutomationSubscription.getter]: (state, getters) => getters[useSubscriptionByProductType.getter](ProductType.Automation),
	[useTeamFeaturesSubscription.getter]: (state, getters) => getters[useSubscriptionByProductType.getter](ProductType.TeamFeatures),
	[useDefaultPaymentMethod.getter]: (state, getters) => getters[usePaymentMethods.getter][getters[useDefaultPaymentMethodId.getter]],
	[useBillingPeriod.getter]: (state, getters) => (getters[useIsAnnuallyPlan.getter] ? BillingPeriod.annually : BillingPeriod.monthly),
	[useCustomerFeatureSubscription.getter]: (state, getters) => getters[useSubscriptionByProductType.getter](ProductType.CustomerFeatures),
	[useCreditsPlans.getter]: (state, getters) => getters[usePlansByProductType.getter](ProductType.Credits),
	[useTeamPlans.getter]: (state, getters) => getters[usePlansByProductType.getter](ProductType.TeamFeatures),
	[useAutomationPlans.getter]: (state, getters) => getters[usePlansByProductType.getter](ProductType.Automation),
	[useCustomerPlans.getter]: (state, getters) => getters[usePlansByProductType.getter](ProductType.CustomerFeatures),
	[useSubscriptionByProductType.getter]: (state, getters) => (productType) =>
		Object.values(getters[useSubscriptions.getter]).find(
			(subscription) => BillingService.getSubscriptionProductType(subscription) === productType
		),
	[usePlans.getter]: (state, getters) => {
		let allPlans = [];
		Object.values(getters[useProducts.getter]).forEach((product) => {
			allPlans = [...allPlans, ...product.plans];
		});
		return arrayToObject(allPlans, 'refId');
	},
	[useEntitlement.getter]: (state, getters) => (feature_id) => getters[useEntitlements.getter][feature_id],
	[useHasCapability.getter]: (state, getters) => (feature_id) => getters[useEntitlement.getter](feature_id)?.has_access,
	[useUpNextRequiredPlanToUseData.getter]: (state, getters) => (feature_id) => getters[useEntitlementPlans.getter]?.[feature_id],
	[useUpNextRequiredPlanToUse.getter]: (state, getters) => (feature_id) =>
		getters[useUpNextRequiredPlanToUseData.getter](feature_id)?.plan_id,

	[useFeatureUsageHistory.getter]: (state, getters) => (featureId) => getters[useUsageHistory.getter][featureId],
	[useFeatureUsageAnalyticsHistory.getter]: (state, getters) => (featureId) => getters[useUsageAnalytics.getter][featureId],
	[useOveragesEstimationCosts.getter]: (state) => (overagesFeatureId) => state.overagesEstimatedCost?.[overagesFeatureId],
};

export default getters;
